import { z } from 'zod';
import { CustomElementsEnum } from '@trinity/custom-pages-registry-types';

// DAG Types (shared across UI, APIs, and chat-api)
export const dagNodeTypeEnum = z.enum(['form', 'decision', 'auto']);

// Stored data and older clients may still carry a legacy `expression`; z.object strips unknown keys.
export const dagEdgeConditionSchema = z.object({
  description: z.string().optional(),
});

export const dagEdgeSchema = z.object({
  id: z.string(),
  from: z.string(), // nodeId
  to: z.string(), // nodeId
  condition: dagEdgeConditionSchema.optional(),
  priority: z.number().optional(), // tie-breaker for multiple valid edges
});

export const dagNodeSchema = z.object({
  id: z.string(),
  type: dagNodeTypeEnum,
  name: z.string().optional(),
  config: z.record(z.string(), z.unknown()).optional(), // node-type specific config
});

export const dagPolicySchema = z.object({
  maxHops: z.int().positive().optional(),
  defaultRetry: z
    .object({
      retries: z.int().nonnegative().prefault(0),
      backoffMs: z.int().nonnegative().prefault(0),
    })
    .optional(),
  nodeTimeoutMs: z.int().positive().optional(),
});

export const workflowDagSchema = z.object({
  nodes: z.array(dagNodeSchema).prefault([]),
  edges: z.array(dagEdgeSchema).prefault([]),
  startNodeId: z.string().optional(),
  endNodeIds: z.array(z.string()).optional(),
});

export const iconnectSyncMappingSchema = z.object({
  nonTextareaMap: z.record(z.string(), z.string()),
  textareaKeys: z.array(z.string()),
  allSurveyQuestions: z.record(z.string(), z.string()),
  surveyTargetRecordType: z.string().optional(),
  questionResponseRecordType: z.string().optional(),
  surveyTargetFields: z.record(z.string(), z.string()).optional(),
});

export const iconnectSyncConfigSchema = z.object({
  enabled: z.boolean().prefault(false),
  syncToIconnectButtonText: z.string().optional(),
  surveyName: z.string().optional(),
  mapping: iconnectSyncMappingSchema.optional(),
});

export const workflowIntroConfigSchema = z.object({
  warningMessage: z.string(),
  showSpeechOption: z.boolean(),
  showManualButton: z.boolean(),
  showFileUploadOption: z.boolean().prefault(false),
  showCancelButton: z.boolean(),
  extractionPrompt: z.string().optional(),
  disclaimerEnabled: z.boolean().prefault(false),
  disclaimerTitle: z.string().optional(),
  disclaimerText: z.string().optional(),
  submitButtonText: z.string().optional(),
  iconnectSync: iconnectSyncConfigSchema.optional(),
  introResponse: z
    .object({
      value: z.string(),
      attachments: z.array(z.unknown()).optional(),
    })
    .optional(),
  dashboardElement: CustomElementsEnum.nullable().optional(),
});

export const workflowToolConfigSchema = z.object({
  toolName: z.string(),
  contentToolName: z.string(),
  toolDescription: z.string(),
  contentToolDescription: z.string(),
  triggerPhrases: z.array(z.string()),
  contentExamples: z.array(z.string()),
});

export const workflowUiConfigSchema = z.object({
  successMessage: z.string(),
  responseMessage: z.string(),
  suggestedFollowups: z.array(z.string()),
  introConfig: workflowIntroConfigSchema,
});

export const workflowMetadataSchema = z.object({
  responseType: z.string().optional(),
  sendToEndpoint: z.boolean().optional(),
  dataConnectionIdentifier: z.string().optional(),
  endpoint: z.string().optional(),
});

export const formWorkflowRegistrySchema = z.object({
  id: z.number(),
  workflowKey: z.string(),
  name: z.string(),
  description: z.string().nullable().optional(),
  features: z.array(z.string()),
  tools: workflowToolConfigSchema,
  ui: workflowUiConfigSchema,
  examples: z.array(z.string()),
  metadata: workflowMetadataSchema,
  // DAG support (optional for back-compat)
  dag: workflowDagSchema.optional(),
  dagVersion: z.int().optional(),
  policy: dagPolicySchema.optional(),
  createdOn: z.coerce.date(),
  updatedOn: z.coerce.date(),
  createdBy: z.number().nullable().optional(),
});

export const listFormWorkflowsResponseSchema = z.object({
  items: z.array(formWorkflowRegistrySchema),
});

export const getFormWorkflowResponseSchema = z.object({
  item: formWorkflowRegistrySchema,
});

export const createFormWorkflowBodySchema = z.object({
  workflowKey: z.string().min(1),
  name: z.string().min(1),
  description: z.string().optional(),
  features: z.array(z.string()).optional(),
  tools: workflowToolConfigSchema,
  ui: workflowUiConfigSchema,
  examples: z.array(z.string()).optional(),
  metadata: workflowMetadataSchema,
  // DAG support
  dag: workflowDagSchema.optional(),
  dagVersion: z.int().optional(),
  policy: dagPolicySchema.optional(),
});

export const updateFormWorkflowBodySchema = z.object({
  workflowKey: z.string().min(1).optional(),
  name: z.string().min(1).optional(),
  description: z.string().optional(),
  features: z.array(z.string()).optional(),
  tools: workflowToolConfigSchema.optional(),
  ui: workflowUiConfigSchema.optional(),
  examples: z.array(z.string()).optional(),
  metadata: workflowMetadataSchema.optional(),
  // DAG support
  dag: workflowDagSchema.optional(),
  dagVersion: z.int().optional(),
  policy: dagPolicySchema.optional(),
});

export const formWorkflowParamsSchema = z.object({
  workflowKey: z.string().min(1),
});

export const formWorkflowByIdParamsSchema = z.object({
  workflowId: z.coerce.number(),
});

export type WorkflowIntroConfig = z.infer<typeof workflowIntroConfigSchema>;
export type IconnectSyncMapping = z.infer<typeof iconnectSyncMappingSchema>;
export type IconnectSyncConfig = z.infer<typeof iconnectSyncConfigSchema>;
export type WorkflowToolConfig = z.infer<typeof workflowToolConfigSchema>;
export type WorkflowUiConfig = z.infer<typeof workflowUiConfigSchema>;
export type WorkflowMetadata = z.infer<typeof workflowMetadataSchema>;
export type FormWorkflowRegistry = z.infer<typeof formWorkflowRegistrySchema>;
export type ListFormWorkflowsResponse = z.infer<typeof listFormWorkflowsResponseSchema>;
export type GetFormWorkflowResponse = z.infer<typeof getFormWorkflowResponseSchema>;
export type CreateFormWorkflowBody = z.infer<typeof createFormWorkflowBodySchema>;
export type UpdateFormWorkflowBody = z.infer<typeof updateFormWorkflowBodySchema>;
export type FormWorkflowParams = z.infer<typeof formWorkflowParamsSchema>;

export type DAGNodeType = z.infer<typeof dagNodeTypeEnum>;

// DAG Validation types
export const dagValidationResultSchema = z.object({
  valid: z.boolean(),
  errors: z.array(z.string()),
  warnings: z.array(z.string()),
});

export const updateDagBodySchema = z.object({
  dag: workflowDagSchema,
  dagVersion: z.int().optional(),
  policy: dagPolicySchema.optional(),
});

export const getDagResponseSchema = z.object({
  dag: workflowDagSchema.nullable(),
});

export type DagValidationResult = z.infer<typeof dagValidationResultSchema>;
export type UpdateDagBody = z.infer<typeof updateDagBodySchema>;
export type GetDagResponse = z.infer<typeof getDagResponseSchema>;

export interface WorkflowConfig {
  workflowKey: string;
  toolName: string;
  contentToolName: string;
  successMessage: string;
  responseMessage: string;
  suggestedFollowups: string[];
  introConfig: WorkflowIntroConfig;
  toolDescription: string;
  contentToolDescription: string;
  triggerPhrases: string[];
  contentExamples: string[];
  responseType?: string;
  metadata?: WorkflowMetadata;
}

// Alias for backward compatibility
export type WorkflowType = string;

export function nameToWorkflowKey(name: string): string {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');
}

export function generateToolNames(workflowKey: string): { toolName: string; contentToolName: string } {
  const kebabKey = workflowKey.replace(/_/g, '-');
  return {
    toolName: `${kebabKey}-tool`,
    contentToolName: `${kebabKey}-tool-content`,
  };
}

export const formWorkflowSubmissionDataSchema = z.object({
  queryId: z.string(),
  workflowType: z.string(),
  formParentId: z.number(),
  formVersion: z.string(),
  formData: z.array(
    z.object({
      fieldLabel: z.string(),
      value: z.unknown(),
    })
  ),
  aiState: z
    .object({
      originalExtraction: z.record(z.string(), z.unknown()).optional(),
      userTranscript: z.string().optional(),
      formParentId: z.number().optional(),
      formVersion: z.string().optional(),
    })
    .optional(),
  userId: z.number(),
  brandIdentifier: z.string(),
});

export const formWorkflowStateUpdateDataSchema = z.object({
  queryId: z.string(),
  workflowType: z.string(),
  formState: z.string().optional(),
  formData: z.array(
    z.object({
      fieldLabel: z.string(),
      value: z.unknown(),
    })
  ),
});

export const workflowMatchResultSchema = z.object({
  workflow: z.object({
    workflowKey: z.string(),
    toolName: z.string(),
    contentToolName: z.string(),
    successMessage: z.string(),
    responseMessage: z.string(),
    suggestedFollowups: z.array(z.string()),
    introConfig: workflowIntroConfigSchema,
    toolDescription: z.string(),
    contentToolDescription: z.string(),
    triggerPhrases: z.array(z.string()),
    contentExamples: z.array(z.string()),
    responseType: z.string().optional(),
  }),
  confidence: z.number(),
  reasoning: z.string(),
});

export type FormWorkflowSubmissionData = z.infer<typeof formWorkflowSubmissionDataSchema>;
export type FormWorkflowStateUpdateData = z.infer<typeof formWorkflowStateUpdateDataSchema>;
export type WorkflowMatchResult = z.infer<typeof workflowMatchResultSchema>;

export type WorkflowDAG = z.infer<typeof workflowDagSchema>;
export type DAGNode = z.infer<typeof dagNodeSchema>;
export type DAGEdge = z.infer<typeof dagEdgeSchema>;
export type DAGPolicy = z.infer<typeof dagPolicySchema>;

export function generateDefaultToolDescriptions(workflowName: string): {
  toolDescription: string;
  contentToolDescription: string;
} {
  const lowerName = workflowName.toLowerCase();
  if (lowerName.length === 0) {
    return {
      toolDescription: '',
      contentToolDescription: '',
    };
  }
  return {
    toolDescription: `Useful for when you need to ${lowerName} using the initial form with manual/voice entry options`,
    contentToolDescription: `Useful for when you have detailed ${lowerName} content that should be parsed and used to pre-fill the ${lowerName} form.`,
  };
}
